import { GroupOfSelectable, SelectableValue } from "@tenforce/product-fields"

import { dataAPI } from "./api"
import { RequestErrorDataApi } from "../errors"
import {
    ODataGetArrayResponse,
    ODataGetArrayResponseUppercase,
    ODataGetResourceResponse,
    ODataGetResourceValueResponse,
} from "./models/api/data"
import {
    AggregateResponse,
    ApplicationDTO,
    ApplicationVersionDTO,
    BillTypeDTO,
    DisplayTypeDTO,
    FieldDefinitionDTO,
    GroupDTO,
    ItemDTO,
    OptionDTO,
    OrganizationItemDTO,
    ProbabilityDTO,
    ImpactDTO,
    StatusDTO,
    Type,
    TypeModel,
    UserDTO,
    UserGroupDTO,
} from "./models/selectors/models"
import { IItem } from "../models"
import { formatString } from "../util/Strings"
import { Exceptions, Labels } from "../util/Translations"

const {
    AnErrorOccurredFetchingGroupsFor = 'An error occurred while fetching the groups for ["{0}"].',
    AnErrorOccurredWhileFetchingParents = 'An error occurred while fetching the parents of this item: ["{0}"].',
} = Exceptions
const { Other } = Labels

const OTHER_GROUP_ID = 0

export type LoadChildrenResponse = {
    children: GroupOfSelectable[]
    count: number
}

export function selectorModelMapper<T extends Type>(
    type: T,
): (data: TypeModel[T]) => Omit<SelectableValue, "isSelected"> & {
    groupData: undefined | GroupDTO
} {
    switch (type) {
        case "TF.ApplicationItemField":
            return (data) => {
                const casted = data as ApplicationDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ApplicationVersionItemField":
            return (data) => {
                const casted = data as ApplicationVersionDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: {
                        Id: casted?.Application?.Id ?? OTHER_GROUP_ID,
                        Label: casted?.Application?.Label ?? "",
                    },
                }
            }
        case "TF.BillTypeItemField":
            return (data) => {
                const casted = data as BillTypeDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.DisplayTypeItemField":
            return (data) => {
                const casted = data as DisplayTypeDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.FieldDefinition":
            return (data) => {
                const casted = data as FieldDefinitionDTO
                const parsedId = casted.Id?.startsWith("CF")
                    ? parseInt(casted.Id?.substring(2))
                    : parseInt(casted.Id?.substring(2)) * -1
                let parsedGroupId = 0
                if (casted.Group) {
                    parsedGroupId = casted.Group.Id.startsWith("CFG")
                        ? parseInt(casted.Group.Id.substring(3))
                        : parseInt(casted.Group.Id.substring(3)) * -1
                }

                return {
                    id: parsedId,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: {
                        Id: parsedGroupId || OTHER_GROUP_ID,
                        Label: casted.Group?.Label || "",
                    },
                }
            }
        case "TF.ImpactItemField":
            return (data) => {
                const casted = data as ImpactDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
            return (data) => {
                const casted = data as ItemDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: true,
                    groupData: undefined,
                }
            }
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return (data) => {
                const casted = data as OptionDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: {
                        Id: casted?.Group?.Id ?? OTHER_GROUP_ID,
                        Label: casted?.Group?.Label ?? "",
                    },
                }
            }
        case "TF.OrganizationItemField":
            return (data) => {
                const casted = data as OrganizationItemDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ProbabilityItemField":
            return (data) => {
                const casted = data as ProbabilityDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
            return (data) => {
                const casted = data as StatusDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return (data) => {
                const casted = data as UserGroupDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return (data) => {
                const casted = data as UserDTO
                return {
                    id: casted.Id,
                    name: casted.Label,
                    isActive: casted.IsActive,
                    groupData: {
                        Id: casted.OrganizationUnit?.Id || OTHER_GROUP_ID,
                        Label: casted.OrganizationUnit?.Label || "",
                    },
                }
            }

        default:
            return (data) => ({
                id: data.Id as number,
                name: "",
                isActive: true,
                groupData: undefined,
            })
    }
}

export const getChildrenForGroup = async <T extends Type>(
    groupId: number,
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
): Promise<LoadChildrenResponse> => {
    const filterQuery = filterQueryMapper(filter, type)

    const groupType = selectorToGroupTypeMapper[type]
    const filterByGroup = groupId
        ? `${groupType}/Id eq ${groupId}`
        : `${groupType} eq null`

    const parameters = {
        $filter: filter
            ? `${filterByGroup} and (${filterQuery})`
            : `${filterByGroup}`,
        $top: top,
        $skip: skip,
        $count: true,
        $expand: groupType,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<TypeModel[T]>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                children: parseSelectorData(response.value, type),
                count: response["@count"],
            } as LoadChildrenResponse
        })
        .catch(() => {
            return { children: [], count: 0 } as LoadChildrenResponse
        })
}

export const getGroups = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 20,
): Promise<GroupOfSelectable | null> => {
    const filterQuery = filterQueryMapper(filter, type)
    const groupType = selectorToGroupTypeMapper[type]

    const completeFilterQuery = filter
        ? `filter(${filterQuery})/groupby((${groupType}/Label,${groupType}/Id),aggregate($count as Total))`
        : `groupby((${groupType}/Label,${groupType}/Id),aggregate($count as Total))`

    const parameters = {
        $apply: completeFilterQuery,
        $top: top,
        $skip: skip,
        $count: true,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<TypeModel[T] & AggregateResponse>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return parseGroupData(response, type)
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredFetchingGroupsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getValues = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
    filter = "",
    skip = 0,
    top = 1000,
): Promise<GroupOfSelectable | null> => {
    const filterQuery = filterQueryMapper(filter, type)

    const parameters = {
        $filter: !!filterQuery ? filterQuery : undefined,
        $top: top,
        $skip: skip,
        $count: true,
        $expand: selectorToGroupTypeMapper[type],
    }

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/SelectableValues`,
            parameters,
        )
        .then((response) => {
            return {
                ...parseResponseData(response, type),
                totalChildrenCount: response["@count"],
            } as GroupOfSelectable
        })
}

export const getSelectedValues = async <T extends Type>(
    itemId: number,
    fieldId: string,
    type: T,
): Promise<GroupOfSelectable | null> => {
    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${type}/Value`,
            {
                $expand: selectorToGroupTypeMapper[type],
            },
        )
        .then((response) => parseResponseData(response, type))
}
export const getItemAncestry = async (itemId: number): Promise<IItem[]> => {
    const parents = []
    let id = itemId
    let shouldContinueSearch = true
    // Escape hatch to make sure we don't fetch never ending hierarchies
    let index = 0
    while (shouldContinueSearch && index < 10) {
        const parent = await getItemParent(id)
        if (parent === null) {
            shouldContinueSearch = false
        } else {
            parents.push(parent)
            id = parent.id
        }
        index++
    }
    if (index === 10 && shouldContinueSearch) {
        console.warn(
            `Stopped fetching parents for item [${itemId}] as there seems to be too many.`,
        )
    }
    return parents.reverse()
}

export const getItemParent = async (itemId: number): Promise<IItem | null> => {
    try {
        const parent = await dataAPI.getJSON<ItemDTO>(`Items/${itemId}/Parent`)
        return {
            id: parent.Id,
            number: parent.Number,
            title: parent.Label,
        }
    } catch (error) {
        if (error instanceof Response) {
            if (error.status === 404) {
                return null
            }
            const errorMessage = formatString(
                AnErrorOccurredWhileFetchingParents,
                itemId,
            )
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}

export const parseResponseData = <T extends Type>(
    response:
        | ODataGetArrayResponse<TypeModel[T]>
        | ODataGetResourceResponse<TypeModel[T]>,
    type: T,
): GroupOfSelectable | null => {
    if (!("value" in response) && response.Id === undefined) {
        return null
    }

    const responseChildren = "value" in response ? response.value : [response]

    return createSelectorGroups(responseChildren, type)
}

export const parseValueResponseData = <T extends Type>(
    response:
        | ODataGetArrayResponseUppercase<TypeModel[T]>
        | ODataGetResourceValueResponse<TypeModel[T]>,
    type: T,
): GroupOfSelectable | null => {
    if (!("Value" in response)) {
        return null
    }
    const responseChildren = Array.isArray(response.Value)
        ? response.Value
        : [response.Value]

    return createSelectorGroups(responseChildren, type)
}

export const parseGroupData = <T extends Type>(
    response: ODataGetArrayResponse<TypeModel[T] & AggregateResponse>,
    type: T,
): GroupOfSelectable | null => {
    if (!("value" in response)) {
        return null
    }

    const children = "value" in response ? response.value : [response]
    const mapper = selectorModelMapper(type)
    const responseChildren = children.map((child) => {
        const mapped = mapper(child)
        const name = mapped.groupData?.Label
        const mappedChild = {
            id: mapped.groupData?.Id,
            name: !!name ? name : Other,
            totalChildrenCount: child.Total,
            selectedChildrenCount: 0,
        }
        return mappedChild
    })

    return {
        id: -1,
        children: responseChildren,
        name: "",
        selectedChildrenCount: 0,
        totalChildrenCount: responseChildren.length,
    } as GroupOfSelectable
}

export const parseSelectorData = <T extends Type>(
    response: TypeModel[T] | TypeModel[T][],
    type: T,
): GroupOfSelectable[] => {
    if (!response) {
        return []
    }

    const responseChildren = Array.isArray(response) ? response : [response]
    const mapper = selectorModelMapper(type)
    const childrenToBe = responseChildren.map((child) => {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
            totalChildrenCount: 0,
            selectedChildrenCount: 0,
        }
        return mappedChild
    })
    return childrenToBe
}

export const parseItemPickerData = (response: ItemDTO[] | ItemDTO): IItem[] => {
    if (!response) {
        return []
    }

    const responseChildren = Array.isArray(response) ? response : [response]
    return responseChildren.map((child) => {
        return {
            id: child.Id,
            title: child.Label,
            number: child.Number,
        }
    })
}

export const createSelectorGroups = <T extends Type>(
    responseChildren: TypeModel[T][],
    type: T,
): GroupOfSelectable | null => {
    const mapper = selectorModelMapper(type)

    const groupsDict: Record<number, GroupOfSelectable> = {}
    if (typeHasGroups(type)) {
        groupsDict[OTHER_GROUP_ID] = {
            id: OTHER_GROUP_ID,
            children: [],
            name: Other,
            selectedChildrenCount: 0,
            totalChildrenCount: 0,
        }
    }
    const children: GroupOfSelectable[] = []
    for (const child of responseChildren) {
        const mapped = mapper(child)
        const mappedChild = {
            ...mapped,
            totalChildrenCount: 0,
            selectedChildrenCount: 0,
        }
        if (mapped.groupData) {
            let targetGroup = groupsDict[mapped.groupData.Id]
            if (!groupsDict[mapped.groupData.Id]) {
                groupsDict[mapped.groupData.Id] = targetGroup = {
                    id: mapped.groupData.Id,
                    name: mapped.groupData.Label,
                    totalChildrenCount: 0,
                    selectedChildrenCount: 0,
                    children: [],
                }
            }
            targetGroup.children?.push(mappedChild)

            targetGroup.selectedChildrenCount =
                targetGroup.children?.length ?? 0
        } else {
            children.push(mappedChild)
        }
    }

    children.push(
        ...Object.values(groupsDict)
            .filter((group) => group?.children?.length)
            .map((group) => {
                const mapped = {
                    ...group,
                    totalChildrenCount: group?.children?.length,
                } as GroupOfSelectable
                return mapped
            }),
    )

    return {
        id: -1,
        name: "",
        totalChildrenCount: children.length,
        selectedChildrenCount: 0,
        children,
    }
}

export const typeHasGroups = (type: Type): boolean => {
    switch (type) {
        case "TF.ApplicationVersionItemField":
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return true
        case "TF.ApplicationItemField":
        case "TF.BillTypeItemField":
        case "TF.DisplayTypeItemField":
        case "TF.FieldDefinition":
        case "TF.ImpactItemField":
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
        case "TF.OrganizationItemField":
        case "TF.ProbabilityItemField":
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return false
    }
}

const filterQueryMapper = (filter: string, type: Type): string => {
    if (!filter) {
        return ""
    }
    if (typeHasGroups(type)) {
        return `contains(Label, '${filter}') or contains(${selectorToGroupTypeMapper[type]}/Label, '${filter}')`
    }
    return `contains(Label,'${filter}')`
}

export const selectorToSingularTypeMapper: Record<Type, string> = {
    "TF.ApplicationItemField": "TF.Application",
    "TF.ApplicationVersionItemField": "TF.ApplicationVersion",
    "TF.BillTypeItemField": "TF.BillType",
    "TF.DisplayTypeItemField": "TF.DisplayType",
    "TF.FieldDefinition": "TF.FieldDefinition",
    "TF.ImpactItemField": "TF.Impact",
    "TF.ItemItemField": "TF.Item",
    "TF.ItemsItemField": "TF.Item",
    "TF.OptionItemField": "TF.Option",
    "TF.OptionsItemField": "TF.Option",
    "TF.OrganizationItemField": "TF.OrganizationUnit",
    "TF.ProbabilityItemField": "TF.Probability",
    "TF.PriorityItemField": "TF.ItemPriority",
    "TF.StatusItemField": "TF.ItemStatus",
    "TF.UserGroupItemField": "TF.UserGroup",
    "TF.UserGroupsItemField": "TF.UserGroup",
    "TF.UserItemField": "TF.User",
    "TF.UsersItemField": "TF.User",
} as const

export const selectorToGroupTypeMapper: Record<Type, string | undefined> = {
    "TF.ApplicationItemField": undefined,
    "TF.ApplicationVersionItemField": "Application",
    "TF.BillTypeItemField": undefined,
    "TF.DisplayTypeItemField": undefined,
    "TF.FieldDefinition": undefined,
    "TF.ImpactItemField": undefined,
    "TF.ItemItemField": undefined,
    "TF.ItemsItemField": undefined,
    "TF.OptionItemField": "Group",
    "TF.OptionsItemField": "Group",
    "TF.OrganizationItemField": undefined,
    "TF.ProbabilityItemField": undefined,
    "TF.PriorityItemField": undefined,
    "TF.StatusItemField": undefined,
    "TF.UserGroupItemField": undefined,
    "TF.UserGroupsItemField": undefined,
    "TF.UserItemField": "OrganizationUnit",
    "TF.UsersItemField": "OrganizationUnit",
}
